import type { QuestionnaireData, AIGeneratedContent } from '@data/publicPlans';
import { devLog } from '@utils/devLog';
import { normalizePricingBreakdown as normalizePricingBands } from '@utils/pricingBreakdownUtils';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8080';

/** Production base URL already ends with /api — avoid /api/api/... paths. */
function planGenApiUrl(endpoint: string): string {
	const clean = endpoint.startsWith('/api/') ? endpoint.replace('/api/', '/') : endpoint;
	return `${API_BASE_URL}${clean.startsWith('/') ? clean : `/${clean}`}`;
}

export type GenerateAIPlanOptions = {
	/** Optional: user's requested changes, e.g. "different colour fence", "more flowers", "whole thing more modern" */
	editInstructions?: string;
	/**
	 * Optional: image URL that should be used as the direct edit base.
	 * For iterative "Change something?" flows this should be the latest generated after image.
	 */
	editBaseImageUrl?: string;
	/**
	 * Optional: original before photo URL for context while regenerating from an edited base.
	 */
	originalBeforeImageUrl?: string;
	/**
	 * Optional: current plan content to revise in-place during edit mode.
	 * When present, backend can preserve unchanged plan parts and patch only requested changes.
	 */
	existingPlan?: AIGeneratedContent;
	/** Optional UI language for generated content (en, de, hr). */
	language?: 'en' | 'de' | 'hr';
	/** Optional authenticated user id (lets backend resolve company country rules). */
	userId?: string | number;
	/** Optional explicit country code (ISO-2) for legal rule warnings. */
	countryCode?: string;
};

/**
 * Generate AI-powered garden plan using OpenAI
 * This replaces the mock implementation with real AI generation
 */
type UiLanguage = 'en' | 'de' | 'hr';

function pickUiLanguage(options?: GenerateAIPlanOptions): UiLanguage {
	const raw =
		options?.language ||
		((typeof localStorage !== 'undefined' ? localStorage.getItem('gardenHub.language') : null) as UiLanguage | null) ||
		'en';
	return raw === 'de' || raw === 'hr' ? raw : 'en';
}

function tPlanGen(lang: UiLanguage, en: string, de: string, hr: string): string {
	return lang === 'de' ? de : lang === 'hr' ? hr : en;
}

export async function generateAIPlan(
	questionnaire: QuestionnaireData,
	options?: GenerateAIPlanOptions
): Promise<AIGeneratedContent> {
	const uiLanguage = pickUiLanguage(options);

	try {
		// Upload before image if it's a data URL
		let beforeImageUrl = questionnaire.photoBeforeUrl;
		if (beforeImageUrl?.startsWith('data:')) {
			beforeImageUrl = await uploadBeforeImage(beforeImageUrl, uiLanguage);
		}

		if (!beforeImageUrl) {
			throw new Error(
				tPlanGen(
					uiLanguage,
					'Before image is required to generate your plan',
					'Für die Planerstellung wird ein Vorher-Foto benötigt.',
					'Za izradu plana potrebna je početna fotografija.'
				)
			);
		}

		const editInstructions = options?.editInstructions?.trim();
		let editBaseImageUrl = options?.editBaseImageUrl?.trim() || undefined;
		let originalBeforeImageUrl = options?.originalBeforeImageUrl?.trim() || undefined;

		if (editBaseImageUrl?.startsWith('data:')) {
			editBaseImageUrl = await uploadBeforeImage(editBaseImageUrl, uiLanguage);
		}
		if (originalBeforeImageUrl?.startsWith('data:')) {
			originalBeforeImageUrl = await uploadBeforeImage(originalBeforeImageUrl, uiLanguage);
		}
		devLog('Calling AI generation endpoint...', editInstructions ? '(with edit instructions)' : '');
		
		// Call backend AI generation endpoint
		// In edit mode, always use the latest generated image as the primary edit base.
		// This keeps compatibility with older backends that only read before_image_url.
		const effectiveBeforeImageUrl = editInstructions && editBaseImageUrl
			? editBaseImageUrl
			: beforeImageUrl;
		const body: Record<string, unknown> = {
			questionnaire,
			before_image_url: effectiveBeforeImageUrl,
			ui_language: uiLanguage
		};
		if (options?.userId != null && String(options.userId).trim() !== '') {
			body.user_id = Number(options.userId);
		}
		if (options?.countryCode && /^[A-Za-z]{2}$/.test(options.countryCode)) {
			body.country_code = options.countryCode.toUpperCase();
		}
		if (editInstructions) {
			body.edit_instructions = editInstructions;
		}
		if (editBaseImageUrl) {
			body.edit_base_image_url = editBaseImageUrl;
		}
		if (originalBeforeImageUrl) {
			body.original_before_image_url = originalBeforeImageUrl;
		}
		if (options?.existingPlan) {
			body.existing_plan = options.existingPlan;
		}
		const callGenerate = async () =>
			fetch(planGenApiUrl('/api/generate-ai-plan'), {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(body)
			});

		let response = await callGenerate();
		// Respect retryable backend responses so temporary provider overload does not immediately fail the UX.
		if (!response.ok && [500, 502, 503, 504].includes(response.status)) {
			const firstAttemptText = await response.text().catch(() => '');
			let firstAttemptParsed: any = null;
			try {
				firstAttemptParsed = firstAttemptText ? JSON.parse(firstAttemptText) : null;
			} catch {
				firstAttemptParsed = null;
			}

			const isRetryable = Boolean(firstAttemptParsed?.details?.retryable);
			const retryAfterSeconds = Number(firstAttemptParsed?.details?.retry_after) || 0;
			const retryDelayMs = isRetryable
				? Math.min(Math.max(retryAfterSeconds, 2), 20) * 1000
				: 1200;

			await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
			response = await callGenerate();
		}

		if (!response.ok) {
			const text = await response.text().catch(() => '');
			let parsed: any = null;
			try {
				parsed = text ? JSON.parse(text) : null;
			} catch {
				parsed = null;
			}
			const errorMessage =
				parsed?.error ||
				parsed?.details?.error_message ||
				parsed?.details?.hint ||
				normalizeApiErrorMessage(text, response.status, response.statusText, uiLanguage);
			console.error('AI generation failed:', errorMessage);
			throw new Error(errorMessage);
		}

		let data: any = null;
		try {
			data = await response.json();
		} catch {
			const text = await response.text().catch(() => '');
			throw new Error(normalizeApiErrorMessage(text, response.status, response.statusText, uiLanguage));
		}
		
		if (!data.success || !data.data) {
			throw new Error(
				tPlanGen(
					uiLanguage,
					'Invalid response from plan generation service',
					'Ungültige Antwort vom Planerstellungsdienst.',
					'Neispravan odgovor usluge za izradu plana.'
				)
			);
		}
		
		devLog('AI generation successful!');

		const generatedContent: AIGeneratedContent = {
			photoAfterUrl: normalizeString(data.data.photoAfterUrl),
			contentLanguage: uiLanguage,
			planDescription: normalizeString(data.data.planDescription, 'Your personalized garden plan is ready.'),
			keyFeatures: normalizeStringArray(data.data.keyFeatures),
			instructions: normalizeStringArray(data.data.instructions),
			timeline_tasks: normalizeTimelineTasks(data.data.timeline_tasks),
			tools: normalizeTools(data.data.tools),
			pricingBreakdown: normalizePricingBreakdown(data.data.pricingBreakdown),
			required_skills: normalizeStringArray(data.data.required_skills),
			recommended_team_size: normalizePositiveNumber(data.data.recommended_team_size),
			instruction_metadata: normalizeInstructionMetadata(data.data.instruction_metadata),
			selected_component_coverage: normalizeSelectedComponentCoverage(data.data.selected_component_coverage),
			generation_warnings: normalizeStringArray(data.data.generation_warnings),
			generatedAt: data.data.generatedAt || new Date().toISOString()
		};

		return generatedContent;
	} catch (error) {
		console.error('AI generation error:', error);
		// Re-throw to let caller handle it (they can show error message to user)
		throw error;
	}
}

function normalizeString(value: unknown, fallback = ''): string {
	if (typeof value === 'string') return value;
	if (value == null) return fallback;
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	try {
		return JSON.stringify(value);
	} catch {
		return fallback;
	}
}

function normalizeStringArray(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value
		.map((item) => normalizeString(item).trim())
		.filter((item) => item.length > 0);
}

function normalizePositiveNumber(value: unknown): number | undefined {
	const n = Number(value);
	if (!Number.isFinite(n) || n <= 0) return undefined;
	return Math.round(n);
}

function normalizePricingBreakdown(value: any): AIGeneratedContent['pricingBreakdown'] {
	return normalizePricingBands({
		min: Number(value?.min ?? 0),
		mid: Number(value?.mid ?? 0),
		max: Number(value?.max ?? 0),
		materials: Array.isArray(value?.materials)
			? value.materials
					.map((m: any) => ({
						name: normalizeString(m?.name),
						quantity: Number(m?.quantity ?? 0),
						unit: normalizeString(m?.unit),
						unitPrice: Number(m?.unitPrice ?? 0),
						total: Number(m?.total ?? 0)
					}))
					.filter((m: any) => m.name)
			: [],
		labor: Array.isArray(value?.labor)
			? value.labor
					.map((l: any) => ({
						name: normalizeString(l?.name),
						hours: Number(l?.hours ?? 0),
						ratePerHour: Number(l?.ratePerHour ?? 0),
						total: Number(l?.total ?? 0)
					}))
					.filter((l: any) => l.name)
			: []
	});
}

function normalizeTools(value: unknown): AIGeneratedContent['tools'] {
	if (!Array.isArray(value)) return [];
	if (value.length === 0) return [];
	if (typeof value[0] === 'string') {
		return normalizeStringArray(value);
	}
	return value
		.map((tool: any) => ({
			name: normalizeString(tool?.name),
			description: normalizeString(tool?.description)
		}))
		.filter((tool: any) => tool.name);
}

function normalizeTimelineTasks(value: unknown): AIGeneratedContent['timeline_tasks'] {
	if (!Array.isArray(value)) return [];
	return value
		.map((task: any) => ({
			title: normalizeString(task?.title),
			description: normalizeString(task?.description),
			dayStart: Number(task?.dayStart ?? 1),
			dayEnd: Number(task?.dayEnd ?? Number(task?.dayStart ?? 1)),
			dependsOnSteps: Array.isArray(task?.dependsOnSteps)
				? task.dependsOnSteps.map((v: any) => Number(v)).filter((v: number) => Number.isFinite(v))
				: undefined,
			seasonal_ban_candidate: Boolean(task?.seasonal_ban_candidate ?? task?.seasonalBanCandidate ?? false),
			seasonal_ban_reason: normalizeString(task?.seasonal_ban_reason ?? task?.seasonalBanReason ?? ''),
			required_skills_for_step: normalizeStringArray(task?.required_skills_for_step),
			required_gardeners_for_step: normalizePositiveNumber(task?.required_gardeners_for_step)
		}))
		.filter((task: any) => task.title);
}

function normalizeInstructionMetadata(value: unknown): AIGeneratedContent['instruction_metadata'] {
	if (!Array.isArray(value)) return [];
	return value.map((step: any) => ({
		required_skills_for_step: normalizeStringArray(step?.required_skills_for_step),
		required_gardeners_for_step: normalizePositiveNumber(step?.required_gardeners_for_step) ?? 1
	}));
}

function normalizeSelectedComponentCoverage(value: unknown): AIGeneratedContent['selected_component_coverage'] {
	if (!value || typeof value !== 'object') return undefined;
	const data = value as Record<string, unknown>;
	return {
		selected: normalizeStringArray(data.selected),
		included: normalizeStringArray(data.included),
		missing: normalizeStringArray(data.missing)
	};
}

function normalizeApiErrorMessage(
	text: string,
	status: number,
	statusText: string,
	lang: UiLanguage = 'en'
): string {
	const trimmed = (text || '').trim();
	const looksLikeHtml = /<html|<body|<title/i.test(trimmed);
	const isTimeout = /request timeout|timed out|takes too long|gateway timeout/i.test(trimmed);

	if (isTimeout || status === 504 || status === 524) {
		return tPlanGen(
			lang,
			'Plan generation is taking longer than usual. Please try again in a minute.',
			'Die Planerstellung dauert derzeit länger als üblich. Bitte versuchen Sie es in einer Minute erneut.',
			'Izrada plana traje duže nego obično. Pokušajte ponovno za minutu.'
		);
	}
	if (status === 500 && looksLikeHtml) {
		return tPlanGen(
			lang,
			'The server returned an internal error while generating your plan. Please try again shortly.',
			'Beim Erstellen Ihres Plans ist auf dem Server ein interner Fehler aufgetreten. Bitte versuchen Sie es in Kürze erneut.',
			'Poslužitelj je pri generiranju plana vratio internu pogrešku. Pokušajte ponovno uskoro.'
		);
	}
	if (!trimmed) {
		return `HTTP ${status}: ${statusText}`;
	}
	if (looksLikeHtml) {
		return `HTTP ${status}: ${statusText}`;
	}
	return trimmed.slice(0, 240);
}

/**
 * Upload before image if it's a data URL
 */
async function uploadBeforeImage(dataUrl: string, lang?: UiLanguage): Promise<string> {
	const uiLang = lang ?? pickUiLanguage();
	const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8080';
	
	// Convert data URL to File
	const arr = dataUrl.split(',');
	const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
	const bstr = atob(arr[1]);
	let n = bstr.length;
	const u8arr = new Uint8Array(n);
	while (n--) {
		u8arr[n] = bstr.charCodeAt(n);
	}
	const file = new File([u8arr], 'before.jpg', { type: mime });
	
	const formData = new FormData();
	formData.append('image', file);
	formData.append('type', 'plan');
	formData.append('subtype', 'before');
	
	const response = await fetch(planGenApiUrl('/api/upload'), {
		method: 'POST',
		body: formData
	});
	
	if (!response.ok) {
		throw new Error(
			tPlanGen(
				uiLang,
				'Failed to upload before image',
				'Hochladen des Vorher-Fotos ist fehlgeschlagen.',
				'Prijenos početne fotografije nije uspio.'
			)
		);
	}
	
	const data = await response.json();
	return data.data?.url || data.url;
}

